import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import Blade from '@/components/ui/Blade/Blade';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { PaymentDeviceRentalRequestStatus, PaymentDeviceRentalStatus } from '@/models/common/PaymentDeviceEnums';
import { PaymentDeviceDetailsDtoIn, PaymentDeviceRentalRequestDtoIn } from '@/models/in/PaymentDeviceDtoIn';
import { MANAGEMENT_DEVICES_URL, formatDeviceDate, formatPickupDay, isAwaitingPickup } from '@/helpers/paymentDevices';
import { DeviceStatusChip, RequestStatusChip } from './DeviceStatusChip';
import { RentalRow } from './RentalRows';
import { DeviceActionTarget, DeviceStatusAction } from './dialogs/DeviceActionDialog';
import styles from '../FundsManagement/FundBlade/FundBlade.module.css';

interface RequestBladeProps {
  request: PaymentDeviceRentalRequestDtoIn;
  onClose: () => void;
  onApprove: (request: PaymentDeviceRentalRequestDtoIn) => void;
  onReject: (request: PaymentDeviceRentalRequestDtoIn) => void;
  onCollect: (request: PaymentDeviceRentalRequestDtoIn) => void;
  onReopen: (request: PaymentDeviceRentalRequestDtoIn) => void;
  onRentalAction: (action: DeviceStatusAction, target: DeviceActionTarget) => void;
}

const formatTimestamp = (value: string) => new Date(value).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' });

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex flex-col gap-0.5">
    <span className={styles.personMeta}>{label}</span>
    <span className="body-sm-semibold">{children}</span>
  </div>
);

/**
 * A rental request's card: what the coordinator asked for, what the office decided, and - once
 * approved - the device it got. Hand-over, return and cancelling the reservation act on the rental
 * that ties the two, so they are offered here as well as on the device and in the fund's blade.
 */
const RequestBlade = ({ request, onClose, onApprove, onReject, onCollect, onReopen, onRentalAction }: RequestBladeProps) => {
  const { t } = useTranslation();
  const rental = request.rental;
  const { data: deviceDetails } = useGetAuthApi<PaymentDeviceDetailsDtoIn>(rental ? `${MANAGEMENT_DEVICES_URL}/${rental.deviceId}` : '');
  const device = deviceDetails?.device;
  const rentalEvents = rental ? (deviceDetails?.events ?? []).filter((event) => event.rentalId === rental.id) : [];
  const target: DeviceActionTarget | null = rental ? { deviceId: rental.deviceId, deviceNumber: rental.deviceNumber, rental } : null;

  const actionButton = (label: string, onClick: () => void, danger = false) => (
    <button type="button" className={styles.rowEditBtn} style={danger ? { color: 'var(--font-error, #c0392b)' } : undefined} onClick={onClick}>
      {label}
    </button>
  );

  const headerTop = (
    <div className="flex items-center gap-3">
      <span className="body-lr-semibold text-(--font-body-4)">{t('paymentDevices.requestBlade.title', { id: request.id })}</span>
      <RequestStatusChip status={request.status} />
    </div>
  );

  const reason = request.status === PaymentDeviceRentalRequestStatus.Rejected && request.rejectionReason
    ? t(`paymentDevices.rejectionReason.${request.rejectionReason}`)
    : null;

  return (
    <Blade handleXclick={onClose} headerTop={headerTop} headerBottom={<></>} width="520px">
      <div className={`${styles.content} custom-scrollbar px-6 pb-6`}>
        <section className="flex flex-col gap-4">
          <h3 className={styles.sectionTitle}>{t('paymentDevices.requestBlade.request')}</h3>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t('paymentDevices.columns.fund')}>{request.fundId} | {request.fundName}</Field>
            <Field label={t('paymentDevices.columns.submittedBy')}>
              {request.submittedByName || request.submittedByEmail}
              <span className={`${styles.personMeta} block`}>{formatTimestamp(request.submittedAt)}</span>
            </Field>
            <Field label={t('paymentDevices.columns.startDate')}>{formatDeviceDate(request.startDate)}</Field>
            <Field label={t('paymentDevices.columns.expectedReturnDate')}>{formatDeviceDate(request.expectedReturnDate)}</Field>
            <Field label={t('paymentDevices.columns.pickupArea')}>{t(`paymentDevices.pickupArea.${request.pickupArea}`)}</Field>
          </div>
          {request.notes && <Field label={t('paymentDevices.columns.notes')}><span className="whitespace-pre-line">{request.notes}</span></Field>}
          {request.status === PaymentDeviceRentalRequestStatus.Pending && (
            <div className="flex flex-wrap gap-2">
              {actionButton(t('paymentDevices.actions.approve'), () => onApprove(request))}
              {actionButton(t('paymentDevices.actions.reject'), () => onReject(request), true)}
            </div>
          )}
        </section>

        {request.reviewedAt && (
          <>
            <div className={styles.line} />
            <section className="flex flex-col gap-4">
              <h3 className={styles.sectionTitle}>{t('paymentDevices.requestBlade.decision')}</h3>
              <div className="grid grid-cols-2 gap-4">
                <Field label={t('paymentDevices.requestBlade.reviewedBy')}>
                  <span dir="ltr">{request.reviewedByEmail}</span>
                  <span className={`${styles.personMeta} block`}>{formatTimestamp(request.reviewedAt)}</span>
                </Field>
                {reason && <Field label={t('paymentDevices.columns.reason')}>{reason}</Field>}
              </div>
              {request.reviewNote && <Field label={t('paymentDevices.requestBlade.reviewNote')}><span className="whitespace-pre-line">{request.reviewNote}</span></Field>}
              {isAwaitingPickup(request) && (
                <>
                  <p className="body-sm-semibold rounded-lg bg-(--surface-grey-lowest) px-3 py-2">
                    {request.holdUntil
                      ? t('paymentDevices.requestBlade.heldUntil', { day: formatPickupDay(request.holdUntil) })
                      : t('paymentDevices.requestBlade.held')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {actionButton(t('paymentDevices.actions.handOver'), () => onCollect(request))}
                  </div>
                </>
              )}
              {request.expiredAt && (
                <p className="body-sm-semibold text-(--font-error,#c0392b)">
                  {t('paymentDevices.requestBlade.expired', { day: formatPickupDay(request.holdUntil) })}
                </p>
              )}
            </section>
          </>
        )}

        {request.status === PaymentDeviceRentalRequestStatus.Cancelled && (
          <>
            <div className={styles.line} />
            <div className="flex flex-wrap gap-2">
              {actionButton(t('paymentDevices.actions.reopen'), () => onReopen(request))}
            </div>
          </>
        )}

        {rental && target && (
          <>
            <div className={styles.line} />
            <section className="flex flex-col gap-4">
              <h3 className={styles.sectionTitle}>{t('paymentDevices.requestBlade.device')}</h3>
              <div className="grid grid-cols-2 gap-4">
                <Field label={t('paymentDevices.columns.deviceNumber')}><span dir="ltr">{rental.deviceNumber}</span></Field>
                <Field label={t('paymentDevices.requestBlade.deviceStatus')}>
                  {device ? <DeviceStatusChip status={device.status} /> : '…'}
                </Field>
              </div>
              {device?.notes && <Field label={t('paymentDevices.columns.notes')}><span className="whitespace-pre-line">{device.notes}</span></Field>}
              <RentalRow
                rental={rental}
                heading={t('paymentDevices.requestBlade.rental')}
                actions={rental.status === PaymentDeviceRentalStatus.Reserved ? (
                  <>
                    {actionButton(t('paymentDevices.actions.handOver'), () => onRentalAction('handOver', target))}
                    {actionButton(t('paymentDevices.actions.cancelAssignment'), () => onRentalAction('cancelAssignment', target), true)}
                  </>
                ) : rental.status === PaymentDeviceRentalStatus.Active ? (
                  <>
                    {actionButton(t('paymentDevices.actions.editHandOverDate'), () => onRentalAction('editHandOverDate', target))}
                    {actionButton(t('paymentDevices.actions.return'), () => onRentalAction('return', target))}
                  </>
                ) : undefined}
              />
              {rentalEvents.length > 0 && (
                <div className={styles.personList}>
                  {rentalEvents.map((event) => (
                    <div key={event.id} className={styles.personRow}>
                      <span className="body-sm-semibold">{t(`paymentDevices.eventType.${event.eventType}`)}</span>
                      <span className={styles.personMeta}>
                        <span>{formatTimestamp(event.at)}</span>
                        {event.byEmail && (
                          <>
                            <span className={styles.metaDot}>•</span>
                            <span dir="ltr">{event.byEmail}</span>
                          </>
                        )}
                      </span>
                      {event.note && <span className={styles.personMeta}>{event.note}</span>}
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </Blade>
  );
};

export default RequestBlade;
